import { apiUtils } from '../hooks';
import { throwApiError } from '../utils';
import { API_ENDPOINTS } from './apiEndpoints';

type BrandSummaryEnterprise = {
  enterprise_id: string;
  enterprise_name: string;
  hotel_count: number;
};
export interface BrandSummary {
  avg_alo_score_from_hotel: number;
  avg_e_score_from_hotel: number;
  avg_s_score_from_hotel: number;
  avg_g_score_from_hotel: number;
  avg_alo_score: number;
  avg_e_score: number;
  avg_s_score: number;
  avg_g_score: number;
  e: {
    biodiversity: number;
    climate: number;
    energy: number;
    waste: number;
    water: number;
  };
  s: {
    community: number;
    human_rights: number;
    responsible_sourcing: number;
    accessibility: number;
  };
  g: {
    auditability: number;
    training: number;
    strategy: number;
  };
  avg_e_biodiversity: number | null;
  avg_e_climate: number | null;
  avg_e_energy: number | null;
  avg_e_waste: number | null;
  avg_e_water: number | null;
  avg_s_community: number | null;
  avg_s_human_rights: number | null;
  avg_s_responsible_sourcing: number | null;
  avg_s_accessibility: number | null;
  avg_g_auditability: number | null;
  avg_g_training: number | null;
  avg_g_strategy: number | null;
  total_properties: number;
  total_markets: number;
  total_countries: number;
  total_enterprises: number;
  scored_hotels: number;
  scored_hotels_percentage: number;
  top_enterprises: BrandSummaryEnterprise[];
}
export interface EnterpriseData {
  id: string;
  name: string;
  total_hotels: number;
  scored_hotels: number;
  avg_alo_score: number;
}
interface ProgramScore {
  avg_alo_score_from_hotel: number;
  avg_e_score_from_hotel: number;
  avg_s_score_from_hotel: number;
  avg_g_score_from_hotel: number;
  avg_alo_score: number;
  avg_e_score: number;
  avg_s_score: number;
  avg_g_score: number;
  e: {
    Biodiversity: number;
    Climate: number;
    Energy: number;
    Waste: number;
    'Water Stewardship': number;
  };
  s: {
    'Community & Cultural Stewardship': number;
    'Human Rights, Justice & Wellbeing': number;
    'Responsible Sourcing': number;
    Accessibility: number;
  };
  g: {
    Auditability: number;
    'Training & Engagement': number;
    'Strategy & Management': number;
  };
}
interface Country {
  country_code: string;
  country_name: string;
}
export interface EnterpriseSummary {
  total_properties: number;
  total_countries: number;
  total_markets: number;
  scored_hotels: number;
  unscored_hotels: number;
  scored_hotels_percentage: number;
  program_score: ProgramScore;
  countries: Country[];
}

export interface EnterpriseHotelData {
  id: string;
  hotel_location_name: string;
  hotel_location_city: string;
  hotel_location_state: string;
  hotel_location_country_code: string;
  hotel_location_country: string;
  hotel_location_latitude: number;
  hotel_location_longitude: number;
  score_alo: number;
  score_e: number;
  score_s: number;
  score_g: number;
  e__bio: number | null;
  e__cli: number | null;
  e__ene: number | null;
  e__was: number | null;
  e__wat: number | null;
  s__com: number | null;
  s__hum: number | null;
  s__res: number | null;
  s__acc: number | null;
  g__aud: number | null;
  g__tra: number | null;
  g__str: number | null;
  is_scored: boolean;
  total_count: string;
  hotel_image: string | null;
}
interface BrandSummaryResponse {
  message: 'Brand summary retrieved successfully';
  data: BrandSummary;
}
interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}
interface EnterpriseListResponse {
  message: 'Brand enterprises summary retrieved successfully';
  data: EnterpriseData[];
  pagination: PaginationData;
}
interface EnterpriseSummaryResponse {
  message: 'Enterprise summary retrieved successfully';
  data: EnterpriseSummary;
}
interface EnterpriseHotelsListResponse {
  message: 'Enterprise hotels retrieved successfully';
  data: EnterpriseHotelData[];
  pagination: PaginationData;
}
export interface HotelData {
  index?: number;
  id: string;
  giata_id: string | null;
  sabre_id: string | null;
  brand: string | null;
  chain: string | null;
  alo_hotel_status: string;
  alo_submission_status: string;
  alo_resubmission_status: string | null;
  alo_resubmission_status_id: string | null;
  alo_hotel_status_free_trial_type: string;
  hotel_location_name: string;
  hotel_location_city: string;
  hotel_location_state: string;
  hotel_location_country: string;
  hotel_location_country_code: string;
  hotel_location_latitude: number;
  hotel_location_longitude: number;
  score_alo: number | null;
  score_e: number | null;
  score_s: number | null;
  score_g: number | null;
  created_date: string;
  last_modified_date: string;
  questionnaire_type: string;
  submission_status_description: string | null;
  submission_status_name: string;
  resubmission_status_name: string | null;
  hotel_status_name: string;
  marketing_gallery_attachment_key: string | null;
  rn: string;
  fundamental_score: {
    Accessibility: number;
    Auditability: number;
    Biodiversity: number;
    Climate: number;
    'Community + Cultural Stewardship': number;
    Energy: number;
    'Human Rights, Justice + Wellbeing': number;
    'Responsible Sourcing': number;
    'Strategy + Management': number;
    'Training + Engagement': number;
    Waste: number;
    'Water Stewardship': number;
  };
  hotel_image: string | null;
}
interface BrandHotelsResponse {
  message: 'Hotels retrieved successfully';
  success: boolean;
  data: HotelData[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
  filters: {
    brandId: string;
    assignedBrandHotels: boolean;
    onlyFasttrack: boolean;
    submissionStatuses: string[];
    primarySortKey: string;
    primarySortOrder: string;
    secondarySortKey: string;
    secondarySortOrder: string;
  };
  metadata: {
    all_count: string;
    action_required_count: string;
    under_review_count: string;
    completed_count: string;
    awaiting_approval_count: string;
    scored_hotels_count: string;
    released_to_hotel_count: string;
    published_hotels_count: string;
    fastrack_count: string;
  };
}

export interface BrandHotelTravelData {
  mission_statement: string | null;
  brand_logo_attachments: Array<{
    attachment_id: string;
    attachment_key: string;
    original_filename: string;
    preview_url: string;
  }>;
  marketing_gallery_attachments: Array<{
    attachment_id: string;
    attachment_key: string;
    original_filename: string;
    preview_url: string;
  }>;
  ppp_gallery_attachments: Array<{
    attachment_id: string;
    attachment_key: string;
    original_filename: string;
    preview_url: string;
  }>;
  promotional_video_attachments: Array<{
    attachment_id: string;
    attachment_key: string;
    original_filename: string;
    preview_url: string;
  }>;
}
interface HotelTravelFacingResponse {
  success: boolean;
  message: string;
  data: BrandHotelTravelData;
}

interface Certification {
  name: string;
  info_text: string;
}

interface Certifications {
  e: Certification[];
  g: Certification[];
  s: Certification[];
}

interface EnvironmentalScoresDetailed {
  Biodiversity: number;
  Climate: number;
  Energy: number;
  Waste: number;
  'Water Stewardship': number;
  renewable_energy_tracking: boolean;
}

interface SocialScoresDetailed {
  'Community & Cultural Stewardship': number;
  'Human Rights, Justice & Wellbeing': number;
  'Responsible Sourcing': number;
  Accessibility: number;
}

interface GovernanceScoresDetailed {
  Auditability: number;
  'Training & Engagement': number;
  'Strategy & Management': number;
}

interface ScoresDetailed {
  e: EnvironmentalScoresDetailed;
  s: SocialScoresDetailed;
  g: GovernanceScoresDetailed;
}

interface CertificationsAndStandards {
  e: Certification[];
  g: Certification[];
  s: Certification[];
}

interface PerformanceCategory {
  e: string[];
  s: string[];
  g: string[];
}

interface PerformanceOverview {
  'Certifications and Standards': CertificationsAndStandards;
  Strengths: PerformanceCategory;
  Progressing: PerformanceCategory;
  'Limited Focus': PerformanceCategory;
}

export interface BrandHotelDetail {
  id: string;
  giata_id: string | null;
  sabre_id: string | null;
  gds_sabre_id: string | null;
  gds_sabre_hotel_chain_code: string | null;
  gds_bcd_travel_tsh_id: string | null;
  gds_galileo_apollo_id: string | null;
  gds_worldspan_id: string | null;
  brand: string | null;
  chain: string | null;
  chain_code_override: string | null;
  early_adopter: boolean | null;
  active_consolidated_submission_id: string | null;
  alo_hotel_status: string | null;
  alo_hotel_status_invoice_date_due: string | null;
  alo_hotel_status_free_trial: boolean | null;
  alo_hotel_status_free_trial_type: string | null;
  alo_hotel_status_free_trial_end: string | null;
  alo_submission_status: string;
  alo_resubmission_status: string | null;
  alo_resubmission: boolean | null;
  alo_resubmission_active: boolean;
  alo_submission_published_time: string | null;
  alo_submission_republished_time: string | null;
  alo_submission_reopen_until_time: string | null;
  hotel_location_override: string | null;
  hotel_location_name: string;
  hotel_location_address_label: string | null;
  hotel_location_address_1: string | null;
  hotel_location_address_2: string | null;
  hotel_location_address_3: string | null;
  hotel_location_address_4: string | null;
  hotel_location_address_5: string | null;
  hotel_location_city: string | null;
  hotel_location_state: string | null;
  hotel_location_destination: string | null;
  hotel_location_country: string | null;
  hotel_location_country_code: string | null;
  hotel_location_zip_code: string | null;
  hotel_location_latitude: number | null;
  hotel_location_longitude: number | null;
  hotel_location_phone: string | null;
  hotel_location_email: string | null;
  hotel_location_website: string | null;
  score_alo: number | null;
  score_e: number | null;
  score_s: number | null;
  score_g: number | null;
  extra_data: any | null;
  created_date: string;
  last_modified_date: string;
  last_modified_by: string;
  created_by: string;
  metadata: any | null;
  alo_hotel_status_id: string | null;
  alo_submission_status_id: string | null;
  alo_resubmission_status_id: string | null;
  questionnaire_type: string;
  mission_statement: string | null;
  hotel_sales_phone: string | null;
  alo_submission_released_to_hotel_timestamp: string | null;
  last_giata_images_sync_at: string | null;
  brand_link_id: string | null;
  latest_alo: number | null;
  latest_e: number | null;
  latest_s: number | null;
  latest_g: number | null;
  e__bio: number | null;
  e__cli: number | null;
  e__ene: number | null;
  e__was: number | null;
  e__wat: number | null;
  s__com: number | null;
  s__hum: number | null;
  s__res: number | null;
  s__acc: number | null;
  g__aud: number | null;
  g__tra: number | null;
  g__str: number | null;
  certifications: Certifications;
  scores_detailed: ScoresDetailed;
  performance_overview: PerformanceOverview;
}

interface BrandHotelDetailResponse {
  success: boolean;
  message: string;
  data: {
    brand_id: string;
    hotel: BrandHotelDetail;
  };
}
export interface LinkedBrand {
  id: string;
  name: string;
  description: string;
  created_at: string;
}
interface LinkedBrandsResponse {
  success: true;
  message: string;
  data: LinkedBrand[];
}
class HotelBrandAdminService {
  async getLinkedBrands() {
    try {
      const response = await apiUtils.get<LinkedBrandsResponse>(
        API_ENDPOINTS.hotelBrandAdmin.linkedBrands
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching linked brands');
      return null;
    }
  }
  async getBrandSummary(brandId: string) {
    try {
      const response = await apiUtils.get<BrandSummaryResponse>(
        API_ENDPOINTS.hotelBrandAdmin.brandSummary,
        {
          brand_id: brandId,
        }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching brand summary');
      return null;
    }
  }

  async getEnterpriseList(params?: Record<string, any>) {
    try {
      const response = await apiUtils.get<EnterpriseListResponse>(
        API_ENDPOINTS.hotelBrandAdmin.enterpriseList,
        { ...params }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching enterprise list');
      return null;
    }
  }

  async getEnterpriseHotelsList(
    enterpriseId: string,
    params?: Record<string, any>
  ) {
    try {
      const response = await apiUtils.get<EnterpriseHotelsListResponse>(
        API_ENDPOINTS.hotelBrandAdmin.enterpriseHotelsList(enterpriseId),
        { ...params }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching enterprise hotels list');
      return null;
    }
  }

  async getEnterpriseSummaryById(enterpriseId: string) {
    try {
      const response = await apiUtils.get<EnterpriseSummaryResponse>(
        API_ENDPOINTS.hotelBrandAdmin.enterpriseSummary(enterpriseId)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching enterprise summary');
      return null;
    }
  }

  async getBrandHotels(params?: Record<string, any>) {
    try {
      const response = await apiUtils.get<BrandHotelsResponse>(
        API_ENDPOINTS.hotelBrandAdmin.brandHotels,
        { ...params }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Error fetching brand hotels');
      return null;
    }
  }

  async getHotelData(hotelId: string) {
    try {
      const response = await apiUtils.get<BrandHotelDetailResponse>(
        API_ENDPOINTS.hotelBrandAdmin.getHotelData(hotelId)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch hotel data');
      return null;
    }
  }

  async getHotelTravelFacingData(hotelId: string) {
    try {
      const response = await apiUtils.get<HotelTravelFacingResponse>(
        API_ENDPOINTS.hotelBrandAdmin.getHotelTravelFacingData(hotelId)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch hotel travel facing data');
      return null;
    }
  }

  async getBrandCountries(brandId?: string): Promise<any> {
    try {
      const params: any = {};
      if (brandId) {
        params.brand_id = brandId;
      }
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelBrandAdmin.brandCountries,
        params
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch brand countries');
      return [];
    }
  }

  async getBrandMarketsForCountry(
    countryCode: string,
    brandId?: string
  ): Promise<any> {
    try {
      const params: any = {};
      if (brandId) {
        params.brand_id = brandId;
      }
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelBrandAdmin.markets(countryCode),
        params
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch brand markets for country');
      return [];
    }
  }
}
export const hotelBrandAdminService = new HotelBrandAdminService();
